import { useRef, type KeyboardEvent } from 'react'

export type TabItem<T extends string> = { value: T; label: string; count?: number }

export const Tabs = <T extends string>({
  label,
  items,
  value,
  onChange,
}: {
  label: string
  items: readonly TabItem<T>[]
  value: T
  onChange: (value: T) => void
}) => {
  const buttonRefs = useRef(new Map<T, HTMLButtonElement>())

  const focusAndSelect = (index: number) => {
    const target = items[index]
    if (target === undefined) return
    onChange(target.value)
    buttonRefs.current.get(target.value)?.focus()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      focusAndSelect((index + 1) % items.length)
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault()
      focusAndSelect((index - 1 + items.length) % items.length)
    } else if (event.key === 'Home') {
      event.preventDefault()
      focusAndSelect(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      focusAndSelect(items.length - 1)
    }
  }

  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {items.map((item, index) => (
        <button
          key={item.value}
          ref={(element) => {
            if (element === null) buttonRefs.current.delete(item.value)
            else buttonRefs.current.set(item.value, element)
          }}
          type="button"
          role="tab"
          id={`tab-${item.value}`}
          aria-selected={item.value === value}
          aria-controls={`tabpanel-${item.value}`}
          tabIndex={item.value === value ? 0 : -1}
          className="tab"
          onClick={() => onChange(item.value)}
          onKeyDown={(event) => onKeyDown(event, index)}
        >
          {item.label}
          {item.count === undefined ? null : (
            <>
              {' '}
              <span className="tab-count">{item.count}</span>
            </>
          )}
        </button>
      ))}
    </div>
  )
}
